import { Fixtuur, ServiceConfig } from '@fixtuur/backpack';
import { connectToParent } from 'penpal';
import * as H from 'preact/hooks';
import * as Actions from '../data/actions';
import * as Core from '../store/core';
import * as Initialisation from '../store/initialisation';
import * as Selections from '../store/selections';
import { SkuCookieEntry } from '../store/middleware/skuDataCollection';
import { useDispatch } from '../store/redux';

import { LogLevel, isLogLevelName } from '@dora/lumberjack';
import { AppConfig } from '../data/appConfig';
import {
    initialiseConfiguration,
    initialiseConfigurationFromSave,
} from './useInitialiseConfiguration/initialiseConfiguration';

export type Cart = {
    conversionId: string;
    products: Array<CartLineItem>;
    price?: number;
};

export type CheckoutLocation = 'finalise step' | 'ar' | 'model viewer' | 'explore';

export type CartLineItem = {
    id: string;
    quantity: number;
};

export type EventData = {
    event: string;
    properties?: {
        [key: string]: any;
    };
};

export type SkuData = {
    skuCookieEntries: Record<string, SkuCookieEntry>;
};

export type ClientEventType = 'iframe_open' | 'add_to_basket';

export type ClientEvent =
    | {
          event: 'iframe_open';
          items: string[];
      }
    | {
          event: 'add_to_basket';
          basket_total?: string;
          items: string[];
      };

// TODO: this should be exposed by the parent package somehow
export interface ParentAPI {
    /** Request that the SDK close the container holding this app */
    close: () => void;
    onCheckout: (data: Cart, checkoutLocation: CheckoutLocation) => void;
    conversionIdChanged: (conversionId: string) => void;
    trackEvent: (data: EventData) => void;
    trackClientEvent: (event: ClientEvent) => void;
    skuDataEvent: (data: SkuData) => void;
}

/**
 * Set up the API for communicating with the parent of the iFrame this app is in
 */
export function useParentApi() {
    const closeAppActions = H.useRef(Actions.create());
    const dispatch = useDispatch();

    // The `useEffect` below initialises the iframe connection and lists
    // no dependenencies as otherwise the connection would be broken and
    // re-established. However, changes to some of these values are a
    // normal part of application lifecycle (e.g service config, locale, etc
    // are set by init). We therefore need to store these in refs for future
    // iframe api calls to use the updated values rather than relying on
    // normal react state and component lifecycles.
    const serviceConfigRef = H.useRef<ServiceConfig.ServiceConfig | undefined>();
    const localeRef = H.useRef<string>();
    const tenantRef = H.useRef<string>();

    const [addToBasket, setAddToBasket] = H.useState<
        undefined | (() => (_cart: Cart, _from: CheckoutLocation) => void)
    >(undefined);
    const [trackClientEvent, setTrackClientEvent] = H.useState<
        undefined | (() => (_event: ClientEvent) => void)
    >(undefined);
    const trackEventRef = H.useRef<(data: EventData) => void>(() => {});
    const skuDataEventRef = H.useRef<
        (data: { skuCookieEntries: Record<string, SkuCookieEntry> }) => void
    >(() => {});
    const updateConversionIdRef = H.useRef<(conversionId: string) => void>(() => {});

    H.useEffect(() => {
        const connection = connectToParent<ParentAPI>({
            methods: {
                init(data: AppConfig) {
                    localeRef.current = data.locale;
                    tenantRef.current = data.tenant;
                    const serviceUrls = {
                        clientId: data.clientId,
                        tenant: data.tenant,
                        models: Core.toastUrl(data.modelsUrl, data.tenant),
                        ar: Core.arUrl(data.arUrl, data.tenant),
                        products: data.productsUrl,
                        links: {
                            service: Core.serviceUrl(data.linksUrl, data.tenant),
                            frontend: Core.serviceUrl(data.dbhqUrl, data.tenant),
                        },
                        layouts: Core.serviceUrl(data.layoutsUrl, data.tenant),
                        modelGenerator: Core.serviceUrl(data.modelGeneratorUrl, data.tenant),
                        compatibility: data.compatibilityServiceUrl,
                        particles: Core.serviceUrl(data.particleServiceUrl, data.tenant),
                        manifest: data.manifestServiceUrl,
                    };
                    serviceConfigRef.current = serviceUrls;
                    dispatch(
                        Core.initialise({
                            open: false,
                            clientId: data.clientId,
                            tenant: data.tenant as Fixtuur.Tenant.TenantId,
                            locale: data.locale,
                            currency: data.currency,
                            featureFlags: data.featureFlags,
                            error: null,
                            logLevel: isLogLevelName(data.logLevel)
                                ? LogLevel[data.logLevel]
                                : LogLevel.Info,
                            serviceUrls,
                            brandColour: data.brandColour,
                            logoSrc: data.logoSrc,
                            environment: data.environment,
                            sentryDsn: data.sentryDsn,
                            measurementUnit: data.measurementUnit,
                        }),
                    );
                },
                startExplorer(data: {
                    productId: string;
                    currentUrl: string;
                    shouldOpenArModal?: boolean;
                    shouldDisplayUnpublished?: boolean;
                }) {
                    dispatch(Core.setOpen()); // TODO loading state?
                    dispatch(Core.setCurrentUrl(data.currentUrl));

                    dispatch(
                        Initialisation.initialiseInspiration({
                            shouldOpenArModal: data.shouldOpenArModal,
                            productId: data.productId,
                            shouldDisplayUnpublished: data.shouldDisplayUnpublished,
                        }),
                    );

                    dispatch(Selections.reset());
                },
                startConfigurationFromSave(data: {
                    configurationId: string;
                    currentUrl: string;
                    shouldOpenArModal: boolean;
                }) {
                    if (!serviceConfigRef.current || !localeRef.current || !tenantRef.current)
                        return;
                    dispatch(Core.setOpen());
                    dispatch(Core.setCurrentUrl(data.currentUrl));
                    initialiseConfigurationFromSave(
                        serviceConfigRef.current,
                        localeRef.current,
                        tenantRef.current,
                        data.configurationId,
                        data.shouldOpenArModal ?? false,
                        dispatch,
                    );
                },
                startConfiguration(data: {
                    configuratorId: string;
                    currentUrl: string;
                    shouldOpenArModal: boolean;
                    startWithSelections?: Record<string, string | number | null>;
                    startWithProduct?: {
                        product: string;
                        options: Record<string, string | number | null>;
                    };
                    startingPieces?: Array<string>;
                }) {
                    if (!tenantRef.current || !localeRef.current || !serviceConfigRef.current)
                        return;
                    dispatch(Core.setOpen());
                    dispatch(Core.setCurrentUrl(data.currentUrl));
                    initialiseConfiguration(
                        tenantRef.current,
                        localeRef.current,
                        data.configuratorId,
                        data.startWithSelections ?? {},
                        data.startWithProduct ?? null,
                        data.startingPieces && data.startingPieces.length > 0
                            ? data.startingPieces
                            : null,
                        data.shouldOpenArModal,
                        dispatch,
                    );
                },
            },
        });
        connection.promise.then(p => {
            // set the core action around closing the app - signalling to the SDK to close the application
            closeAppActions.current = Actions.add(
                'core_close',
                () => {
                    dispatch(Core.setClosed());
                    dispatch(Initialisation.clear());
                    p.close();
                },
                closeAppActions.current,
            );

            if (p.onCheckout) {
                setAddToBasket(() => (cart: Cart, from: CheckoutLocation) => {
                    p.onCheckout(cart, from);
                });
            }

            if (p.trackClientEvent) {
                setTrackClientEvent(() => (event: ClientEvent) => {
                    p.trackClientEvent(event);
                });
            }
            updateConversionIdRef.current = p.conversionIdChanged;
            trackEventRef.current = p.trackEvent;
            skuDataEventRef.current = p.skuDataEvent;
        });
    }, []);

    const enhanceCloseApp = H.useCallback((name: string, action: () => void) => {
        closeAppActions.current = Actions.add(name, action, closeAppActions.current);
    }, []);

    const closeApp = H.useCallback(() => Actions.run(closeAppActions.current), []);
    const updateConversionId = H.useCallback((id: string) => updateConversionIdRef.current(id), []);
    const trackEvent = H.useCallback(
        (event: string, properties?: { [key: string]: any }) =>
            trackEventRef.current({ event, properties }),
        [],
    );
    const skuDataEvent = H.useCallback((skuCookieEntries: Record<string, SkuCookieEntry>) => {
        skuDataEventRef.current({ skuCookieEntries });
    }, []);

    return {
        closeApp,
        enhanceCloseApp,
        addToBasket,
        updateConversionId,
        trackEvent,
        skuDataEvent,
        trackClientEvent,
    };
}
